<!-- src/views/ReporteProveedores.vue -->
<template>
  <div class="reporte-proveedores p-4">
    <!-- Header con título y acciones -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
      <div>
        <h1 class="text-2xl font-bold text-gray-800 dark:text-white">Reporte de Proveedores</h1>
        <p class="text-gray-500 dark:text-gray-400 mt-1">Visualiza y exporta el listado de proveedores con múltiples filtros</p>
      </div>
      <div class="flex gap-2 flex-wrap">
        <button
          @click="imprimirReporteCompleto"
          class="px-4 py-2 bg-gray-800 hover:bg-gray-900 text-white rounded-lg transition-colors flex items-center gap-2"
          :disabled="loading"
        >
          <Icon icon="mdi:printer" width="20" height="20" />
          Imprimir Reporte
        </button>
        <button
          @click="exportarExcel"
          class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-2"
          :disabled="loading"
        >
          <Icon icon="mdi:file-excel" width="20" height="20" />
          Exportar Excel
        </button>
        <button
          @click="exportarPDF"
          class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2"
          :disabled="loading"
        >
          <Icon icon="mdi:file-pdf" width="20" height="20" />
          Exportar PDF
        </button>
      </div>
    </div>

    <!-- Filtros combinados -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6">
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <!-- Filtro por estado -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Estado del Proveedor
          </label>
          <select
            v-model="filters.estado"
            class="input-premium appearance-none"
          >
            <option value="">Todos</option>
            <option value="activos">Activos</option>
            <option value="inactivos">Inactivos</option>
          </select>
        </div>

        <!-- Filtro por tipo de proveedor -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Tipo de Proveedor
          </label>
          <select
            v-model="filters.tipo"
            class="input-premium appearance-none"
          >
            <option value="">Todos los tipos</option>
            <option v-for="tipo in tiposUnicos" :key="tipo" :value="tipo">
              {{ tipo }}
            </option>
          </select>
        </div>

 

        <!-- Filtro por búsqueda -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Buscar proveedor
          </label>
          <input
            type="text"
            v-model="filters.busqueda"
            placeholder="Nombre, RIF, contacto..."
            class="input-premium"
          />
        </div>
      </div>

      <!-- Filtros adicionales -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Contacto
          </label>
          <select
            v-model="filters.contacto"
            class="input-premium appearance-none"
          >
            <option value="">Todos</option>
            <option value="con_contacto">Con contacto (teléfono o email)</option>
            <option value="sin_contacto">Sin contacto</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Mínimo de Productos
          </label>
          <input
            type="number"
            v-model="filters.productos_min"
            placeholder="Desde"
            class="input-premium"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Máximo de Productos
          </label>
          <input
            type="number"
            v-model="filters.productos_max"
            placeholder="Hasta"
            class="input-premium"
          />
        </div>
      </div>

      <!-- Botones de acción -->
      <div class="flex justify-end gap-2 mt-4">
        <button
          @click="applyFilters"
          class="btn-primary flex items-center gap-2"
        >
          <Icon icon="mdi:filter" width="18" height="18" />
          Aplicar Filtros
        </button>
        <button
          @click="clearFilters"
          class="btn-secondary"
        >
          Limpiar Todo
        </button>
      </div>
    </div>

    <!-- Resumen de estadísticas -->
    <div class="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">Total Proveedores</p>
            <p class="text-2xl font-bold text-gray-800 dark:text-white">{{ estadisticas.total || 0 }}</p>
          </div>
          <Icon icon="mdi:account-group" width="32" height="32" class="text-blue-500" />
        </div>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">Activos</p>
            <p class="text-2xl font-bold text-green-600 dark:text-green-400">{{ estadisticas.activos || 0 }}</p>
          </div>
          <Icon icon="mdi:check-circle" width="32" height="32" class="text-green-500" />
        </div>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">Inactivos</p>
            <p class="text-2xl font-bold text-red-600 dark:text-red-400">{{ estadisticas.inactivos || 0 }}</p>
          </div>
          <Icon icon="mdi:close-circle" width="32" height="32" class="text-red-500" />
        </div>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">Con Teléfono</p>
            <p class="text-2xl font-bold text-purple-600 dark:text-purple-400">{{ estadisticas.conTelefono || 0 }}</p>
          </div>
          <Icon icon="mdi:phone" width="32" height="32" class="text-purple-500" />
        </div>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">Con Email</p>
            <p class="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{{ estadisticas.conEmail || 0 }}</p>
          </div>
          <Icon icon="mdi:email" width="32" height="32" class="text-indigo-500" />
        </div>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">Total Productos</p>
            <p class="text-2xl font-bold text-orange-600 dark:text-orange-400">{{ estadisticas.totalProductos || 0 }}</p>
          </div>
          <Icon icon="mdi:package-variant" width="32" height="32" class="text-orange-500" />
        </div>
      </div>
    </div>

    <!-- Tabla de proveedores -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">ID</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Nombre</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">RIF</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Teléfono</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Email</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Contacto</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Tipo</th>
    
              <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Productos</th>
              <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Compras</th>
              <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Estado</th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            <tr v-if="loading">
              <td colspan="11" class="px-6 py-8 text-center">
                <div class="flex justify-center">
                  <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
                <p class="mt-2 text-gray-500">Cargando proveedores...</p>
              </td>
            </tr>
            <tr v-else-if="proveedoresFiltrados.length === 0">
              <td colspan="11" class="px-6 py-8 text-center text-gray-500">
                No se encontraron proveedores con los filtros seleccionados
              </td>
            </tr>
            <tr v-for="proveedor in proveedoresPaginados" :key="proveedor.idProveedor" class="hover:bg-gray-50 dark:hover:bg-gray-700">
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                {{ proveedor.idProveedor }}
              </td>
              <td class="px-6 py-4 text-sm text-gray-900 dark:text-white">
                <div>
                  <div class="font-medium">{{ proveedor.nombre }}</div>
                  <div class="text-xs text-gray-500 dark:text-gray-400">{{ proveedor.direccion || '' }}</div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900 dark:text-white">
                {{ formatearRIF(proveedor.rif) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                {{ formatearTelefono(proveedor.telefono) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                {{ proveedor.email || '—' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                {{ proveedor.contacto || '—' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                <span class="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                  {{ proveedor.tipoProveedor || 'Sin tipo' }}
                </span>
              </td>
          
              <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900 dark:text-white">
                {{ proveedor.totalProductos || 0 }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                {{ proveedor.totalCompras || 0 }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-center">
                <span :class="{
                  'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200': proveedor.estado?.toLowerCase() === 'activo',
                  'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200': proveedor.estado?.toLowerCase() === 'inactivo'
                }" class="px-2 py-1 text-xs font-semibold rounded-full">
                  {{ proveedor.estado === 'activo' ? 'Activo' : 'Inactivo' }}
                </span>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="proveedoresFiltrados.length > 0" class="bg-gray-50 dark:bg-gray-700">
            <tr>
              <td colspan="8" class="px-6 py-3 text-right text-sm font-semibold text-gray-900 dark:text-white">
                TOTALES:
              </td>
              <td class="px-6 py-3 text-right text-sm font-bold text-blue-600 dark:text-blue-400">
                {{ estadisticas.totalProductos }}
              </td>
              <td class="px-6 py-3 text-right text-sm font-bold text-blue-600 dark:text-blue-400">
                {{ estadisticas.totalCompras }}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Paginación -->
      <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <div class="text-sm text-gray-500">
          Mostrando {{ (pagination.page - 1) * pagination.perPage + 1 }} - 
          {{ Math.min(pagination.page * pagination.perPage, pagination.total) }} de {{ pagination.total }} proveedores
          <span v-if="proveedoresOriginales.length !== proveedoresFiltrados.length" class="ml-2 text-blue-600">
            (Filtrados de {{ proveedoresOriginales.length }} totales)
          </span>
        </div>
        <div class="flex gap-2">
          <button
            @click="prevPage"
            :disabled="pagination.page === 1"
            class="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Anterior
          </button>
          <span class="px-3 py-1">
            Página {{ pagination.page }} de {{ pagination.lastPage }}
          </span>
          <button
            @click="nextPage"
            :disabled="pagination.page >= pagination.lastPage"
            class="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Siguiente
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { useToast } from "vue-toastification";
import { 
  obtenerProveedores, 
  buscarProveedores, 
  filtrarProveedoresPorEstado,
  obtenerEstadisticasProveedores,
  obtenerTiposUnicosProveedores,
  exportarExcel,
  exportarPDF,
  formatearRIF,
  formatearTelefono
} from "@/api/proveedores.js";
import { 
  imprimirReporteProveedores, 
  cargarLogo,
  filtrarProveedoresParaReporte
} from "@/utils/reporteProveedoresGenerator";

export default {
  name: 'ReporteProveedores',
  components: {
    Icon
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      loading: false,
      proveedoresOriginales: [],
      filters: {
        estado: '',
        tipo: '',
        especialidad: '',
        contacto: '',
        busqueda: '',
        productos_min: '',
        productos_max: ''
      },
      pagination: {
        page: 1,
        perPage: 15,
        total: 0,
        lastPage: 1
      }
    };
  },
  computed: {
    // Tipos únicos de proveedores
    tiposUnicos() {
      return obtenerTiposUnicosProveedores(this.proveedoresOriginales);
    },
    
    // Especialidades únicas
    especialidadesUnicas() {
      const especialidades = new Set();
      this.proveedoresOriginales.forEach(p => {
        if (p.especialidad) {
          especialidades.add(p.especialidad);
        }
      });
      return Array.from(especialidades).sort();
    },
    
    // Aplicar todos los filtros combinados
    proveedoresFiltrados() {
      let resultado = [...this.proveedoresOriginales];
      
      // 1. Filtro por estado
      if (this.filters.estado) {
        resultado = filtrarProveedoresPorEstado(resultado, this.filters.estado);
      }
      
      // 2. Filtro por tipo
      if (this.filters.tipo) {
        resultado = resultado.filter(p => p.tipoProveedor === this.filters.tipo);
      }
      
      // 3. Filtro por especialidad
      if (this.filters.especialidad) {
        resultado = resultado.filter(p => p.especialidad === this.filters.especialidad);
      }
      
      // 4. Filtro por contacto
      if (this.filters.contacto === 'con_contacto') {
        resultado = resultado.filter(p => 
          (p.telefono && p.telefono.trim() !== '') || (p.email && p.email.trim() !== '')
        );
      } else if (this.filters.contacto === 'sin_contacto') {
        resultado = resultado.filter(p => 
          (!p.telefono || p.telefono.trim() === '') && (!p.email || p.email.trim() === '')
        );
      }
      
      // 5. Filtro por búsqueda
      if (this.filters.busqueda) {
        resultado = buscarProveedores(resultado, this.filters.busqueda);
      }
      
      // 6. Filtro por rango de productos
      if (this.filters.productos_min !== '') {
        resultado = resultado.filter(p => (p.totalProductos || 0) >= parseInt(this.filters.productos_min));
      }
      if (this.filters.productos_max !== '') {
        resultado = resultado.filter(p => (p.totalProductos || 0) <= parseInt(this.filters.productos_max));
      }
      
      return resultado;
    },
    
    // Proveedores paginados
    proveedoresPaginados() {
      const start = (this.pagination.page - 1) * this.pagination.perPage;
      const end = start + this.pagination.perPage;
      return this.proveedoresFiltrados.slice(start, end);
    },
    
    // Estadísticas actualizadas
    estadisticas() {
      return obtenerEstadisticasProveedores(this.proveedoresFiltrados);
    }
  },
  watch: {
    proveedoresFiltrados: {
      handler() {
        this.pagination.total = this.proveedoresFiltrados.length;
        this.pagination.lastPage = Math.ceil(this.pagination.total / this.pagination.perPage);
        if (this.pagination.page > this.pagination.lastPage && this.pagination.lastPage > 0) {
          this.pagination.page = this.pagination.lastPage;
        }
      },
      immediate: true
    }
  },
  async mounted() {
    await cargarLogo();
    await this.loadProveedores();
  },
  methods: {
    formatearRIF,
    formatearTelefono,
    
    async loadProveedores() {
      this.loading = true;
      try {
        const result = await obtenerProveedores('all');
        if (result.success) {
          this.proveedoresOriginales = result.data;
        } else {
          this.proveedoresOriginales = [];
        }
        
        this.pagination.total = this.proveedoresOriginales.length;
        this.pagination.lastPage = Math.ceil(this.pagination.total / this.pagination.perPage);
      } catch (error) {
        console.error('Error cargando proveedores:', error);
        this.proveedoresOriginales = [];
      } finally {
        this.loading = false;
      }
    },
    
    applyFilters() {
      this.pagination.page = 1;
    },
    
    clearFilters() {
      this.filters = {
        estado: '',
        tipo: '',
        especialidad: '',
        contacto: '',
        busqueda: '',
        productos_min: '',
        productos_max: ''
      };
      this.pagination.page = 1;
    },
    
    prevPage() {
      if (this.pagination.page > 1) {
        this.pagination.page--;
      }
    },
    
    nextPage() {
      if (this.pagination.page < this.pagination.lastPage) {
        this.pagination.page++;
      }
    },
    
    async imprimirReporteCompleto() {
      if (this.proveedoresFiltrados.length === 0) {
        this.toast.warning('No hay proveedores que coincidan con los filtros seleccionados');
        return;
      }
      
      await imprimirReporteProveedores(
        this.proveedoresFiltrados,
        this.getTituloReporte(),
        this.getFiltroEstado(),
        this.filters.tipo || null
      );
    },
    
    exportarExcel() {
      if (this.proveedoresFiltrados.length === 0) {
        this.$swal?.fire({
          icon: 'warning',
          title: 'Sin datos',
          text: 'No hay proveedores que coincidan con los filtros'
        });
        return;
      }
      
      const resultado = exportarExcel(this.proveedoresFiltrados, this.getNombreReporte());
      
      if (resultado.success) {
        this.toast.success(`Exportación exitosa: ${resultado.count} proveedores`);
      } else {
        this.toast.error('Error al exportar: ' + resultado.error);
      }
    },
    
    async exportarPDF() {
      if (this.proveedoresFiltrados.length === 0) {
        this.toast.warning('No hay proveedores que coincidan con los filtros');
        return;
      }
      
      const resultado = await exportarPDF(this.proveedoresFiltrados, this.getTituloReporte());
      
      if (resultado.success) {
        this.toast.success(`PDF generado con ${resultado.count} proveedores`);
      } else {
        this.toast.error('Error al generar PDF: ' + resultado.error);
      }
    },
    
    getFiltroEstado() {
      if (this.filters.estado === 'activos') return 'activos';
      if (this.filters.estado === 'inactivos') return 'inactivos';
      if (this.filters.contacto === 'con_contacto') return 'con_contacto';
      if (this.filters.contacto === 'sin_contacto') return 'sin_contacto';
      return 'todos';
    },
    
    getTituloReporte() {
      const partes = [];
      
      // Estado
      if (this.filters.estado) {
        partes.push(this.filters.estado === 'activos' ? 'PROVEEDORES ACTIVOS' : 'PROVEEDORES INACTIVOS');
      }
      
      // Tipo
      if (this.filters.tipo) {
        partes.push(`TIPO: ${this.filters.tipo.toUpperCase()}`);
      }
      
      // Especialidad
      if (this.filters.especialidad) {
        partes.push(`ESPECIALIDAD: ${this.filters.especialidad.toUpperCase()}`);
      }
      
      // Contacto
      if (this.filters.contacto === 'con_contacto') {
        partes.push('CON DATOS DE CONTACTO');
      } else if (this.filters.contacto === 'sin_contacto') {
        partes.push('SIN DATOS DE CONTACTO');
      }
      
      // Búsqueda
      if (this.filters.busqueda) {
        partes.push(`BUSQUEDA: "${this.filters.busqueda}"`);
      }
      
      // Rango de productos
      if (this.filters.productos_min !== '' || this.filters.productos_max !== '') {
        let rango = 'PRODUCTOS ';
        if (this.filters.productos_min !== '' && this.filters.productos_max !== '') {
          rango += `ENTRE ${this.filters.productos_min} Y ${this.filters.productos_max}`;
        } else if (this.filters.productos_min !== '') {
          rango += `≥ ${this.filters.productos_min}`;
        } else if (this.filters.productos_max !== '') {
          rango += `≤ ${this.filters.productos_max}`;
        }
        partes.push(rango);
      }
      
      if (partes.length === 0) {
        return 'REPORTE GENERAL DE PROVEEDORES';
      }
      
      return `REPORTE DE PROVEEDORES - ${partes.join(' | ')}`;
    },
    
    getNombreReporte() {
      const partes = [];
      
      if (this.filters.estado) partes.push(this.filters.estado);
      if (this.filters.tipo) partes.push(`tipo_${this.filters.tipo.replace(/\s/g, '_')}`);
      if (this.filters.especialidad) partes.push(`esp_${this.filters.especialidad.replace(/\s/g, '_')}`);
      if (this.filters.contacto) partes.push(this.filters.contacto);
      if (this.filters.busqueda) partes.push(`busqueda_${this.filters.busqueda.substring(0, 20)}`);
      
      const nombreBase = partes.length > 0 ? partes.join('_') : 'todos';
      return `reporte_proveedores_${nombreBase}`;
    }
  }
};
</script>

<style scoped>
.reporte-proveedores {
  min-height: 100vh;
}
</style>